// One axios instance for the whole client. Its base URL follows the
// configured backend; the request interceptor attaches the bearer token.
import axios from "axios";
import { getConfig, onConfigChange } from "./config";
import { getToken } from "./session";

const apiBase = (baseUrl) => `${baseUrl}/api`;

export const http = axios.create({ baseURL: apiBase(getConfig().baseUrl) });

/** Absolute API root ("https://host/api"); a live binding that follows configureCore. */
export let API = http.defaults.baseURL;
onConfigChange((cfg) => {
  http.defaults.baseURL = apiBase(cfg.baseUrl);
  API = http.defaults.baseURL;
});

// Attach the stored token when a request is sent (not when it is built),
// so a token saved a moment ago is used by the very next call.
http.interceptors.request.use((req) => {
  const token = getToken();
  if (token) req.headers.Authorization = `Bearer ${token}`;
  return req;
});

const AUTH_PATHS = ["/login", "/register", "/auth/lookup"];

/**
 * Call `handler` when an authenticated request answers 401 (expired or
 * invalid session). Auth endpoints are exempt: a wrong password is a 401
 * too and must stay an inline form error. Returns an unsubscribe function.
 */
export function onUnauthorized(handler) {
  const id = http.interceptors.response.use(
    (res) => res,
    (error) => {
      const url = error.config?.url || "";
      const isAuthCall = AUTH_PATHS.some((p) => url.includes(p));
      if (error.response?.status === 401 && !isAuthCall && getToken()) handler(error);
      return Promise.reject(error);
    }
  );
  return () => http.interceptors.response.eject(id);
}

/** Extract a human-readable message from an axios error. */
export function apiErrorDetail(error) {
  const d = error.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    return d.map((x) => (x && typeof x.msg === "string" ? x.msg : JSON.stringify(x))).join(", ");
  }
  return error.message || "Request failed";
}
